import {
	AfterContentChecked,
	AfterContentInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Inject,
	Input,
	NgZone,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges
} from "@angular/core";
import { UntilDestroy } from "@ngneat/until-destroy";
import { AuthenticatedMode, AuthenticatedModeContainer } from "app/app.module";
import { RtlService } from "../../../../../rtl/services/rtl.service";
import { WINDOW_LISTENER_TOKEN, WindowHostListenerService } from "../../../../../window/window.host.listener.service";
import { DeltaEvent } from "../../../../directives/touch.drag/delta.drag.directive";
import { AbstractPopupComponent, POSITION_TOKEN, PX } from "../abstract.popup.component";
import { PopupPositionStore } from "../popup.position.store";


@UntilDestroy()
@Component({
	selector: 'cp-static-popup',
	styleUrls: ['../popup.component.scss'],
	templateUrl: '../popup.component.html'	
})
export class StaticPopupComponent extends AbstractPopupComponent implements OnInit, OnChanges, OnDestroy, AfterContentInit, AfterContentChecked {
	@Input() label;
	@Input() labelParam: string;
	@Input() labelPlain:string;

	@Input() resizable;

	@Input() width: string;
	@Input() height: string;

	@Input() minHeight: string;
	@Input() minWidth: string;

	@Input() autoHeight: boolean;
	@Input() autoHeightAutoReCalc: boolean;

	@Input() isModal = true;
	@Input() contentPadding: boolean;
	@Input() draggable: boolean;

	@Input() positionToken:POSITION_TOKEN;

	@Input() spinner:boolean;
	//public _kendoOffset:Offset;

	protected contentHeightCalculated: boolean;

	protected lastDelta;// = { top: 0, left: 0 };
	protected topDeltaInPx = 0;
	protected leftDeltaInPx = 0;
	protected firstTop:string;

	constructor(
		protected cd: ChangeDetectorRef,
		protected ngZone: NgZone,
		protected positionStore:PopupPositionStore, // before login we dont have this available
		@Inject(WINDOW_LISTENER_TOKEN) protected windowHostListenerService: WindowHostListenerService,
		protected el:ElementRef,
		@Inject(AuthenticatedMode) private  authMode:AuthenticatedModeContainer,
		rtl:RtlService,
	) {
		super(ngZone,cd,positionStore,el,windowHostListenerService,rtl);
	}

	ngOnInit() {
		this.width = this.width || '1700px';
		this.height = !this.autoHeight ? (this.height || '870px') : (this.height || '');
		
		if (this.authMode.mode){
			// a cleaner implementation would be for the position-store to be encpasulated
			// and handle the non-authenticated mode inside, but this is a more significant change for
			// this stage of v&v
			let cachedPosition = this.positionStore.getPoistion(this.positionToken);
			if (cachedPosition){
				this.lastDelta = {
					top: cachedPosition.topPx,
					left:  cachedPosition.leftPx
				}
			}
		}		
		this.lastDelta =  this.lastDelta || {top: 0, left: 0};
		
		// this.windowHostListenerService.getObservable(WindowEvent.resize)
		// 	.pipe(untilDestroyed(this))
		// 	.subscribe(() => this.cd.markForCheck());
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['show'] && this.show && this.autoHeight && this.contentInit) {
			setTimeout(() => {
				var needDetect=false;
				let newHeight = this.calcContentHeight() + PX;
				if (newHeight !== this.height) {
					this.height = newHeight;
					needDetect=true;
				}

				let newWidth = this.calcContentWidth() + PX;
				if (newWidth !== this.width) {
					this.width =newWidth;
					needDetect=true;
				}
				if(needDetect){
					this.cd.detectChanges();
				}
			});
		}
	}

	ngAfterContentChecked() {
		if (this.autoHeight && (this.autoHeightAutoReCalc || !this.contentHeightCalculated) && !this.hidden) {
			this.contentHeightCalculated = true;
			this.checkHeight((height,width)=>{
				this.height=height;
				// dont care about width
			});
		}
	}

	ngOnDestroy() {
		this.clearWindowEventSubscription();
	 }

	protected clearWindowEventSubscription(): void {
		this.windowEventSubscription.unsubscribe();
	}

	get Top() {
		if (this.firstTop){
			return this.firstTop;
		}
		const windowHeight = window.innerHeight;
		const contentHeightInPercent = this.height.endsWith("px")
			? +this.height.substring(0, this.height.length - 2) / windowHeight * 100
			: +this.height.substring(0, this.height.length - 1); // Percent

		let retVal;
		if (!this.draggable) {
			const topInPercent = contentHeightInPercent > 98 ? 1 : (100 - contentHeightInPercent) / 2;
			retVal=topInPercent + '%';
		}else{
			// fix in case there was excessive dragging which should be stopped since the popup should actually be out of the view
			let normalized = this.normalizeNew(contentHeightInPercent,windowHeight,this.lastDelta.top,this.topDeltaInPx);
			retVal=normalized.normalizedTotal+ PX;
			this.topDeltaInPx=normalized.normalizedDelta;
		}
		if (this.height && +this.height.substring(0,this.height.indexOf(PX))>50){
			this.firstTop=retVal;
		}else{
			this.firstTop=undefined;
		}
		return retVal;
	}

	get Left() {
		const windowWidth = window.innerWidth;
		const contentWidthInPercent = this.width.endsWith("px")
			? +this.width.substring(0, this.width.length - 2) / windowWidth * 100
			: +this.width.substring(0, this.width.length - 1); // Percent
		let retVal;
		if (!this.draggable) {
			const leftInPercent = contentWidthInPercent > 98 ? 1 : (100 - contentWidthInPercent) / 2;
			retVal= leftInPercent + '%';
		}else{
			let normalized = this.normalizeNew(contentWidthInPercent,windowWidth,this.lastDelta.left,this.leftDeltaInPx);
			retVal=normalized.normalizedTotal+ PX;
			this.leftDeltaInPx=normalized.normalizedDelta;
		}
		return retVal;
	}

	get Height() {
		return this.autoHeightAutoReCalc?"auto":this.height;
	}

	get Width() {
		return this.width;
	}

	public dragEnded(){
		if (this.authMode.mode){
			this.positionStore.setPoistion(this.positionToken, {
				leftPx: this.lastDelta.left + this.leftDeltaInPx,
				topPx: this.lastDelta.top + this.topDeltaInPx
			});
		}
	}

	headerDeltaDrag({left, top, isFirst}: DeltaEvent) {
		if (isFirst) {
			this.lastDelta = {
				top: this.lastDelta.top + this.topDeltaInPx,
				left: this.lastDelta.left + this.leftDeltaInPx
			};
		}

		this.firstTop=undefined;
		this.topDeltaInPx = top;
		this.leftDeltaInPx = left;
	}
}
